import { Link } from "react-router-dom";
import LogoBadge from "@/components/LogoBadge";
import Footer from "@/components/Footer";

const SectionHeading = ({ children }) => (
  <h2
    className="font-serif text-2xl sm:text-3xl text-[#161618] mt-14 mb-5 tracking-tight"
    style={{ fontFamily: "'Cormorant Garamond', serif" }}
  >
    {children}
  </h2>
);

const Paragraph = ({ children }) => (
  <p className="text-[#3a3a3c] font-light leading-relaxed text-base sm:text-[1.05rem] mb-5">
    {children}
  </p>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 mb-6 pl-1">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-3 text-[#3a3a3c] font-light leading-relaxed"
      >
        <span className="mt-2 inline-block w-1 h-1 rounded-full bg-[#B6AA96] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function Privacy() {
  return (
    <div
      data-testid="privacy-page"
      className="bg-[#F1EDDE] text-[#161618] min-h-screen"
    >
      {/* Minimal header */}
      <header className="border-b border-[#B6AA96]/30">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-home-link"
            className="inline-flex items-center"
          >
            <LogoBadge variant="warmblack" size="sm" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="text-[0.65rem] uppercase tracking-[0.3em] text-[#161618] hover:text-[#B6AA96] transition-colors"
          >
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <div className="text-[0.65rem] uppercase tracking-[0.4em] text-[#B6AA96] mb-6">
          Legal
        </div>
        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#161618] tracking-tight leading-[1.05]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Privacy Policy
        </h1>
        <p className="mt-6 text-[0.7rem] uppercase tracking-[0.3em] text-[#8C8580]">
          Last Updated: 7 June 2026
        </p>

        <div className="mt-10">
          <Paragraph>
            At Stacy Fox Laser and Aesthetics, your privacy and trust are
            treated with the same care and professionalism as your treatment
            experience.
          </Paragraph>
          <Paragraph>
            We are committed to protecting your personal information and
            handling all data responsibly, securely, and in accordance with UK
            data protection laws, including UK GDPR and the Data Protection Act
            2018.
          </Paragraph>

          <SectionHeading>Information We Collect</SectionHeading>
          <Paragraph>We may collect personal information including:</Paragraph>
          <Bullets
            items={[
              "Name",
              "Contact details",
              "Date of birth",
              "Appointment and booking information",
              "Medical and consultation history relevant to treatment safety",
              "Treatment photographs where clinically appropriate",
              "Website usage data through cookies and analytics",
            ]}
          />
          <Paragraph>
            Any medical or consultation information is collected solely to
            ensure treatments are safe, suitable, and tailored to you.
          </Paragraph>

          <SectionHeading>How Your Information Is Used</SectionHeading>
          <Paragraph>Your information may be used to:</Paragraph>
          <Bullets
            items={[
              "Manage appointments and enquiries",
              "Deliver safe and appropriate treatments",
              "Maintain accurate clinical records",
              "Send appointment reminders and aftercare",
              "Improve our services and website experience",
              "Send marketing communications where consent has been given",
            ]}
          />
          <Paragraph>We never sell your personal information.</Paragraph>

          <SectionHeading>Photography & Clinical Images</SectionHeading>
          <Paragraph>
            Clinical photographs may be taken as part of your treatment journey
            for assessment, documentation, and monitoring results.
          </Paragraph>
          <Paragraph>
            Separate written consent will always be obtained before any images
            are used for educational or marketing purposes.
          </Paragraph>

          <SectionHeading>Data Security</SectionHeading>
          <Paragraph>
            We take appropriate measures to protect your information using
            secure systems, restricted access, and professional confidentiality
            standards.
          </Paragraph>
          <Paragraph>
            Your data is only retained for as long as necessary to meet legal,
            insurance, and professional obligations.
          </Paragraph>

          <SectionHeading>Marketing Communications</SectionHeading>
          <Paragraph>
            If you subscribe to our newsletter or marketing updates, you may
            receive:
          </Paragraph>
          <Bullets
            items={[
              "Clinic news",
              "Treatment education",
              "Exclusive offers",
              "Launches and events",
            ]}
          />
          <Paragraph>You may unsubscribe at any time.</Paragraph>

          <SectionHeading>Cookies</SectionHeading>
          <Paragraph>
            Our website may use cookies and analytics tools to improve
            functionality, user experience, and website performance.
          </Paragraph>
          <Paragraph>
            You can manage cookie preferences through your browser settings.
          </Paragraph>

          <SectionHeading>Your Rights</SectionHeading>
          <Paragraph>You have the right to:</Paragraph>
          <Bullets
            items={[
              "Request access to your personal data",
              "Correct inaccurate information",
              "Withdraw consent",
              "Request deletion where appropriate",
              "Object to certain types of processing",
            ]}
          />
          <Paragraph>
            You also have the right to lodge a complaint with the UK
            Information Commissioner's Office (ICO).
          </Paragraph>

          <SectionHeading>Contact</SectionHeading>
          <Paragraph>
            For any privacy-related enquiries, please contact:
          </Paragraph>

          <div className="mt-2 p-6 sm:p-8 border border-[#B6AA96]/40 bg-[#FFFFFF]/40 backdrop-blur-sm">
            <p
              className="font-serif text-xl text-[#161618] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Stacy Fox Laser and Aesthetics
            </p>
            <p className="text-[#3a3a3c] font-light leading-relaxed">
              Email:{" "}
              <a
                href="mailto:sfaesthetics20@gmail.com"
                data-testid="privacy-email-link"
                className="underline decoration-[#B6AA96] underline-offset-4 hover:text-[#B6AA96] transition-colors"
              >
                sfaesthetics20@gmail.com
              </a>
            </p>
            <p className="text-[#3a3a3c] font-light leading-relaxed mt-1">
              Phone:{" "}
              <a
                href="tel:+447513616311"
                data-testid="privacy-phone-link"
                className="underline decoration-[#B6AA96] underline-offset-4 hover:text-[#B6AA96] transition-colors"
              >
                07513 616 311
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
